import { AdminApi, CountryEnum } from 'api';
import useApiCall from 'hooks/use-api-call';
import React, { FunctionComponent, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons } from 'ui/molecules/modal';
import { StateValues } from 'react-use-form-state';
import useCountries from 'src/hooks/use-countries';
import Section from 'src/ui/atoms/section';
import * as Styled from './styled';

export interface EditPersonalDataFields {
  id: string;
  forename: string;
  surname: string;
  birthDate: Date | null | undefined;
  birthPlace: string | undefined;
  nationality: CountryEnum | undefined;
  isSubjectToUsTax: string;
}

export interface EditPersonalDataModalProps {
  onHideEditPersonalData: () => void;
  fetchInvestorDetails: () => void;
  showSuccessModal: () => void;
  values: EditPersonalDataFields;
}

const EditPersonalDataForm = makeModalForm<EditPersonalDataFields>();

const EditPersonalDataModal: FunctionComponent<EditPersonalDataModalProps> = (props) => {
  const { fetchInvestorDetails, onHideEditPersonalData, showSuccessModal = () => {}, values } = props;

  const [investorData, setInvestorData] = useState<EditPersonalDataFields>(values);

  const { makeAuthenticatedApi, withApi } = useApiCall();

  const investorsApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const editPersonalData = (fieldsValues: StateValues<EditPersonalDataFields>) => {
    withApi(async () => {
      const patchBody = {
        id: values.id,
        patchedAdminInvestorUpdateRequest: {
          naturalPerson: {
            ...fieldsValues,
            isSubjectToUsTax: investorData.isSubjectToUsTax === 'true',
          },
        },
      };

      await investorsApi.adminInvestorsPartialUpdate(patchBody);

      showSuccessModal();
      fetchInvestorDetails();
      onHideEditPersonalData();
    });
  };

  return (
    <>
      <Modal onClose={onHideEditPersonalData}>
        <EditPersonalDataForm
          initial={{
            forename: investorData.forename,
            surname: investorData.surname,
            birthDate: investorData.birthDate,
            birthPlace: investorData.birthPlace,
            nationality: investorData.nationality,
            isSubjectToUsTax: investorData.isSubjectToUsTax.toString(),
          }}
          onChange={(values: StateValues<EditPersonalDataFields>) => {
            setInvestorData(values);
          }}
          onSubmit={(values: StateValues<EditPersonalDataFields>) => {
            return editPersonalData(values);
          }}
          i18nKey="dashboardIssuerInvestorDetails.editPersonalDataForm"
        >
          <ModalHeader>
            <Translate name="dashboardIssuerInvestorDetails.editPersonalDataForm.title" />
          </ModalHeader>
          <ModalContent>
            <Styled.Row>
              <EditPersonalDataForm.Group name="forename" required={true}>
                <EditPersonalDataForm.Input />
              </EditPersonalDataForm.Group>
              <EditPersonalDataForm.Group name="surname" required={true}>
                <EditPersonalDataForm.Input />
              </EditPersonalDataForm.Group>
            </Styled.Row>
            <Section spacing="regular">
              <EditPersonalDataForm.Group name="birthDate" required={true}>
                <EditPersonalDataForm.DateInput max={new Date()} />
                {EditPersonalDataForm.Validators.PastDate()}
                {EditPersonalDataForm.Validators.Over18()}
              </EditPersonalDataForm.Group>
              <EditPersonalDataForm.Group name="birthPlace" required={true}>
                <EditPersonalDataForm.Input />
              </EditPersonalDataForm.Group>
              <EditPersonalDataForm.Group name="nationality" required={true}>
                <EditPersonalDataForm.Select
                  options={useCountries()}
                  value={investorData.nationality}
                ></EditPersonalDataForm.Select>
              </EditPersonalDataForm.Group>
            </Section>
            <EditPersonalDataForm.Group name="isSubjectToUsTax" required={true} forceLabelOnNewLine={true}>
              <EditPersonalDataForm.GroupToggle>
                {['true', 'false'].map((option) => (
                  <EditPersonalDataForm.Radio value={option}>
                    <Translate name={`commonOptions.${option}`} />
                  </EditPersonalDataForm.Radio>
                ))}
              </EditPersonalDataForm.GroupToggle>
            </EditPersonalDataForm.Group>
          </ModalContent>
          <ModalFooter>
            <ModalFooterButtons
              actionButtons={[
                {
                  name: 'cancel',
                  content: <Translate name="common.cancel" />,
                  size: 'large',
                  onClick: onHideEditPersonalData,
                },
                {
                  name: 'send',
                  content: <Translate name="common.saveChanges" />,
                  variant: 'primary',
                  size: 'large',
                  type: 'submit',
                },
              ]}
            />
          </ModalFooter>
        </EditPersonalDataForm>
      </Modal>
    </>
  );
};

export default EditPersonalDataModal;
